import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AdCard from "@/components/AdCard";
import { CATEGORIES, COUNTRIES, categoryLabel, isCategoryActive } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X, Inbox, Sparkles } from "lucide-react";

export default function ListingsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("categoria") || "";
  const city = params.get("ciudad") || "";
  const country = params.get("pais") || "";
  const q = params.get("q") || "";

  const inactiveCategory = category && !isCategoryActive(category);

  const [localCity, setLocalCity] = useState(city);
  const [localCountry, setLocalCountry] = useState(country || "España");
  const [localQ, setLocalQ] = useState(q);

  useEffect(() => {
    setLocalCity(city);
    setLocalCountry(country || "España");
    setLocalQ(q);
  }, [city, country, q]);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (category) query.category = category;
    if (city) query.city = city;
    if (country) query.country = country;
    if (q) query.q = q;
    api
      .get("/ads", { params: query })
      .then((r) => setAds(r.data))
      .finally(() => setLoading(false));
  }, [category, city, country, q]);

  const updateParams = (obj) => {
    const p = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => {
      if (v) p.set(k, v);
      else p.delete(k);
    });
    setParams(p);
  };

  const onFilterSubmit = (e) => {
    e.preventDefault();
    updateParams({ ciudad: localCity.trim(), pais: localCountry, q: localQ.trim() });
  };

  const activeCategory = useMemo(
    () => CATEGORIES.find((c) => c.slug === category),
    [category],
  );

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* Header banner */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
          <p className="eyebrow mb-2">
            {activeCategory ? `Sección · ${activeCategory.label}` : "Todos los anuncios"}
          </p>
          <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-[#1A1A1A]">
            {activeCategory ? activeCategory.tagline : "Anuncios de servicios para compartir"}
          </h1>
          {(city || country) && (
            <p className="mt-2 text-[#475569]">
              Mostrando anuncios locales de{" "}
              <span className="font-semibold text-[#1A1A1A]">
                {city}
                {city && country ? ", " : ""}
                {country}
              </span>
            </p>
          )}

          {/* Category chips */}
          <div className="mt-8 flex flex-wrap gap-2">
            <button
              data-testid="chip-all-categories"
              onClick={() => updateParams({ categoria: "" })}
              className={`px-4 h-10 text-sm font-medium border transition-colors ${
                !category
                  ? "bg-[#1A1A1A] text-[#F8FAFC] border-[#1A1A1A]"
                  : "border-[#CBD5E1] text-[#1A1A1A] hover:border-[#3B5FB8]"
              }`}
            >
              Todas
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                data-testid={`chip-category-${c.slug}`}
                onClick={() => c.active && updateParams({ categoria: c.slug })}
                disabled={!c.active}
                aria-disabled={!c.active}
                className={`px-4 h-10 text-sm font-medium border transition-colors inline-flex items-center gap-2 ${
                  !c.active
                    ? "border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed bg-[#F8FAFC]"
                    : category === c.slug
                    ? "bg-[#1A1A1A] text-[#F8FAFC] border-[#1A1A1A]"
                    : "border-[#CBD5E1] text-[#1A1A1A] hover:border-[#3B5FB8]"
                }`}
              >
                {c.label}
                {!c.active && (
                  <span className="text-[9px] font-mono uppercase tracking-widest">próx</span>
                )}
              </button>
            ))}
          </div>

          {/* Filters */}
          <form
            onSubmit={onFilterSubmit}
            data-testid="listings-filter-form"
            className="mt-6 grid gap-3 sm:grid-cols-12 sm:gap-4 bg-[#F8FAFC] border border-[#E2E8F0] p-4"
          >
            <div className="sm:col-span-4">
              <Label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#3B5FB8]">Ciudad</Label>
              <Input
                data-testid="filter-city-input"
                value={localCity}
                onChange={(e) => setLocalCity(e.target.value)}
                placeholder="Ciudad"
                className="rounded-none h-11 border-[#CBD5E1] bg-white mt-1.5"
              />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#3B5FB8]">País</Label>
              <Select value={localCountry} onValueChange={setLocalCountry}>
                <SelectTrigger data-testid="filter-country-select" className="rounded-none h-11 border-[#CBD5E1] bg-white mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3">
              <Label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#3B5FB8]">Buscar</Label>
              <div className="relative mt-1.5">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#3B5FB8]" />
                <Input
                  data-testid="filter-search-input"
                  value={localQ}
                  onChange={(e) => setLocalQ(e.target.value)}
                  placeholder="Palabras clave"
                  className="rounded-none h-11 pl-9 border-[#CBD5E1] bg-white"
                />
              </div>
            </div>
            <div className="sm:col-span-2 sm:pt-[26px]">
              <Button
                type="submit"
                data-testid="filter-submit"
                className="w-full h-11 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#F8FAFC] rounded-none"
              >
                Aplicar
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-[#475569]" data-testid="results-count">
            {loading ? "Cargando..." : `${ads.length} ${ads.length === 1 ? "anuncio" : "anuncios"}`}
            {category ? ` en ${categoryLabel(category)}` : ""}
          </p>
          {(city || country || q) && (
            <button
              data-testid="clear-filters"
              onClick={() => {
                setParams(category ? { categoria: category } : {});
              }}
              className="inline-flex items-center gap-1 text-xs text-[#3B5FB8] link-underline font-medium"
            >
              <X className="w-3 h-3" /> Limpiar filtros
            </button>
          )}
        </div>

        {inactiveCategory ? (
          <div data-testid="inactive-category-notice" className="bg-white border border-[#A3B8DA] p-14 text-center">
            <div className="inline-flex items-center gap-2 border border-[#A3B8DA] bg-[#EEF4FB] px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-[#1E3A8A] mb-6">
              <Sparkles className="w-3 h-3" />
              Próximamente
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1A1A1A] mb-3">
              La sección {categoryLabel(category)} llegará muy pronto
            </h3>
            <p className="text-[#475569] max-w-lg mx-auto mb-8 text-base">
              Empezaremos muy pronto compartiendo <strong className="text-[#1A1A1A]">Pisos</strong> y seguiremos creciendo como comunidad. Cuando abramos {categoryLabel(category)} serás de los primeros en enterarte.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/anuncios?categoria=pisos${city ? `&ciudad=${city}` : ""}${country ? `&pais=${country}` : ""}`)}
                data-testid="cta-go-pisos"
                className="inline-flex items-center gap-2 h-11 px-6 bg-[#1E3A8A] text-[#F8FAFC] font-medium hover:bg-[#152B6B]"
              >
                Ver anuncios de Pisos
              </button>
              <Link to="/" className="text-sm text-[#3B5FB8] link-underline font-medium">
                Volver a la portada
              </Link>
            </div>
          </div>
        ) : loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] animate-pulse">
                <div className="aspect-[4/3] bg-[#EEF2F7]" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[#EEF2F7]" />
                  <div className="h-4 w-2/3 bg-[#EEF2F7]" />
                </div>
              </div>
            ))}
          </div>
        ) : ads.length === 0 ? (
          <div data-testid="empty-state" className="bg-white border border-[#E2E8F0] p-14 text-center">
            <Inbox className="w-10 h-10 mx-auto text-[#3B5FB8] mb-4" />
            <h3 className="font-serif text-2xl font-semibold text-[#1A1A1A] mb-2">
              Aún no hay anuncios aquí
            </h3>
            <p className="text-[#475569] max-w-md mx-auto mb-6 text-sm">
              Sé el primero en publicar en esta sección y ciudad. Es gratis y solo lleva unos minutos.
            </p>
            <Link
              to="/publicar"
              data-testid="empty-publish-cta"
              className="inline-flex items-center gap-2 h-11 px-6 bg-[#1A1A1A] text-[#F8FAFC] font-medium hover:bg-[#2A2A2A]"
            >
              Publicar anuncio
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {ads.map((ad) => (
              <AdCard key={ad.id} ad={ad} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
